import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PrimaryGrid } from '@ui/components/common/Grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { StyledMenu } from '@ui/components/common/Menu';
import ReassignPickupLocationDialog from '../ReassignPickupLocationDialog';
import ReassignVehicleDialog from '../ReassignVehicleDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getUnassignedAttendeesByPickupLocationIdAsync, getAssignedAttendeesByPickupLocationIdAsync, assignAttendeeToVehicleAsync, reassignAttendeesToPickupLocationAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getUnassignedFamilyFestAttendeesByPickupLocationIdAsync, getAssignedFamilyFestAttendeesByPickupLocationIdAsync, reassignFamilyFestAttendeesToPickupLocationAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { assignAttendeeToRouteVehicleAsync } from '@application/features/eventVehicle/eventVehicleThunk';
import { getFamilyFestPickupLocationByIdAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { getPickupLocationByIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { toast } from 'react-toastify';
import { formatRegisteredDate } from '@application/utilities/date-helper';
const LocationUnassignedAttendeeTab = ({ selectedRows, onSelectionChange, filterOpen, onFilterOpenChange, searchValue, locationId, isFamilyFest }) => {
    const { t } = useTranslation();
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { unassignedAttendees: standardUnassignedAttendees } = useAppSelector((state) => state.attendeePickupAssignment);
    const { unassignedAttendees: familyFestUnassignedAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const unassignedAttendees = isFamilyFest ? familyFestUnassignedAttendees : standardUnassignedAttendees;
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [selectedAttendeeForAssign, setSelectedAttendeeForAssign] = useState(null);
    const [selectedAttendeeForReassignPickup, setSelectedAttendeeForReassignPickup] = useState(null);
    const [reassignPickupOpen, setReassignPickupOpen] = useState(false);
    const [assignVehicleOpen, setAssignVehicleOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filters, setFilters] = useState({});
    useEffect(() => {
        if (locationId) {
            if (isFamilyFest) {
                const sort = sortModel[0];
                const sortFieldMap = {
                    company: 'companyname',
                    type: 'attendeetype',
                    registered: 'createdon'
                };
                dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    registeredDate: filters.registered,
                    sortBy: sort ? (sortFieldMap[sort.field] ?? sort.field) : undefined,
                    sortOrder: sort?.sort ?? 'desc'
                }));
            }
            else {
                dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    registeredDate: filters.registered
                }));
            }
        }
    }, [
        dispatch,
        locationId,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        filters,
        isFamilyFest,
        sortModel,
        selectedCompanyIds
    ]);
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    }, []);
    const handleReassignPickup = useCallback(() => {
        handleMenuClose();
        if (menuRow) {
            setSelectedAttendeeForReassignPickup(menuRow);
        }
        setReassignPickupOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleReassignPickupConfirm = useCallback(async (location) => {
        if (selectedAttendeeForReassignPickup) {
            if (isFamilyFest) {
                dispatch(reassignFamilyFestAttendeesToPickupLocationAsync({
                    attendeeIds: [selectedAttendeeForReassignPickup.id],
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully'));
                        // Refresh the unassigned attendees list and location summary counts
                        if (locationId) {
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                            dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        setSelectedAttendeeForReassignPickup(null);
                        setReassignPickupOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed');
                    toast.error(errorMessage);
                });
            }
            else {
                dispatch(reassignAttendeesToPickupLocationAsync({
                    attendeeIds: [selectedAttendeeForReassignPickup.id],
                    pickupLocationId: String(location.id)
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully'));
                        // Refresh the unassigned attendees list and location summary counts
                        if (locationId) {
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                            dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        setSelectedAttendeeForReassignPickup(null);
                        setReassignPickupOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed');
                    toast.error(errorMessage);
                });
            }
        }
    }, [selectedAttendeeForReassignPickup, dispatch, locationId, paginationModel.pageSize, searchValue, isFamilyFest, t]);
    const handleAssignVehicle = useCallback(() => {
        if (menuRow) {
            setSelectedAttendeeForAssign(menuRow);
        }
        handleMenuClose();
        setAssignVehicleOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleAssignConfirm = useCallback(async (vehicle) => {
        if (selectedAttendeeForAssign) {
            if (isFamilyFest) {
                dispatch(assignAttendeeToRouteVehicleAsync({
                    assignmentIds: [selectedAttendeeForAssign.id],
                    routeVehicleId: vehicle.id
                }))
                    .unwrap()
                    .then(() => {
                    toast.success(t('events.eventDetails.transportTab.attendeeAssignedSuccessfully'));
                    if (locationId) {
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                            pickupLocationId: locationId,
                            pageNumber: 1,
                            pageSize: paginationModel.pageSize,
                            searchString: searchValue || undefined
                        }));
                        dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                            pickupLocationId: locationId,
                            pageNumber: 1,
                            pageSize: paginationModel.pageSize
                        }));
                    }
                    setSelectedAttendeeForAssign(null);
                    setAssignVehicleOpen(false);
                })
                    .catch((error) => {
                    toast.error(error?.error || t('events.eventDetails.transportTab.attendeeAssignFailed'));
                });
            }
            else {
                dispatch(assignAttendeeToVehicleAsync({
                    attendeeIds: [selectedAttendeeForAssign.id],
                    vehicleId: vehicle.id
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeAssignedSuccessfully'));
                        // Refresh unassigned list, assigned list, and location summary counts
                        if (locationId) {
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                            dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                            dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize
                            }));
                        }
                        setSelectedAttendeeForAssign(null);
                        setAssignVehicleOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeAssignFailed'));
                    }
                })
                    .catch((error) => {
                    const errorMessage = error?.message || t('events.eventDetails.transportTab.attendeeAssignFailed');
                    toast.error(errorMessage);
                });
            }
        }
    }, [selectedAttendeeForAssign, dispatch, isFamilyFest, locationId, paginationModel.pageSize, searchValue, t]);
    const rowMenuActions = useMemo(() => [
        ...(!isFamilyFest ? [{
                text: t('events.eventDetails.transportTab.reassignPickupLocation'),
                to: handleReassignPickup,
                group: true
            }] : []),
        {
            text: t('events.eventDetails.transportTab.assignVehicle'),
            to: handleAssignVehicle,
            group: true
        }
    ], [t, isFamilyFest, handleReassignPickup, handleAssignVehicle]);
    const columns = useMemo(() => [
        {
            field: 'name',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 150,
            filterField: 'name',
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name || '—' }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'company',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 140,
            filterField: 'company',
            renderCell: (params) => {
                return params.row.companyName || params.row.company || '—';
            }
        },
        {
            field: 'type',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.9,
            minWidth: 110,
            filterField: 'type',
            renderCell: (params) => {
                return params.row.attendeeType || '—';
            }
        },
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.3,
            minWidth: 180,
            filterField: 'registered',
            renderCell: (params) => {
                return formatRegisteredDate(params.row.createdDate);
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.35,
            minWidth: 70,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', width: '100%', children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRow(params.row);
                    }, children: _jsx(MoreHorizIcon, {}) }) }))
        }
    ], [t]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: unassignedAttendees?.items || [], isMultiple: true, onSelectionChange: (model) => onSelectionChange(model), rowSelectionModel: selectedRows, filterOpen: filterOpen, onFilterOpenChange: onFilterOpenChange, showHeaderControls: false, showNewHeaderControls: true, paginationModel: paginationModel, onPaginationModelChange: (newModel) => {
                    setPaginationModel(newModel);
                }, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                }, paginationMode: 'server', rowCount: unassignedAttendees?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [10, 25, 50, 100], hideFooter: false, sx: {
                    height: 'calc(100dvh - 464px)'
                } }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(ReassignPickupLocationDialog, { open: reassignPickupOpen, pickupLocationId: locationId, isFamilyFest: isFamilyFest, onClose: () => {
                    setSelectedAttendeeForReassignPickup(null);
                    setReassignPickupOpen(false);
                }, onConfirm: handleReassignPickupConfirm }), _jsx(ReassignVehicleDialog, { open: assignVehicleOpen, onClose: () => {
                    setSelectedAttendeeForAssign(null);
                    setAssignVehicleOpen(false);
                }, onConfirm: handleAssignConfirm, title: t('events.eventDetails.transportTab.assignVehicle'), isFamilyFest: isFamilyFest })] }));
};
export default LocationUnassignedAttendeeTab;
